import React, { useCallback, useEffect, useState } from "react";
import { Heart, Bug, Lightbulb, Plus, MessageSquare, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { TabLayout } from "@/components/peeps/TabLayout";
import { HomeButton } from "@/components/peeps/HomeButton";
import { Sheet, Field, Input, PrimaryButton, GhostButton, Spinner } from "@/components/peeps/ui";
import { CommentThread } from "@/components/peeps/CommentThread";

const TYPES = [
  { key: "all", label: "All", icon: MessageSquare },
  { key: "feature_request", label: "Ideas", icon: Lightbulb },
  { key: "bug", label: "Bugs", icon: Bug },
];

const STATUS = {
  open: { label: "Open", bg: "#2A2B38", fg: colors.onSurfaceTertiary },
  in_progress: { label: "In Progress", bg: "#2A2140", fg: colors.brandSecondary },
  done: { label: "Done", bg: "#12301F", fg: colors.success },
  wont_fix: { label: "Won't Fix", bg: "#2E1A1D", fg: "#F0A0A0" },
};
const STATUS_ORDER = ["open", "in_progress", "done", "wont_fix"];

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.open;
  return (
    <span data-testid={`feedback-status-${status}`} className="font-display font-medium text-[10.5px] rounded-full px-2.5 py-1" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function FeedbackCard({ post, isAdmin, onVote, onStatus, onEdit, onDelete, expanded, onToggleComments }) {
  const TypeIcon = post.type === "bug" ? Bug : Lightbulb;
  const typeColor = post.type === "bug" ? "#F0A0A0" : colors.brandSecondary;
  const canManage = post.is_mine || isAdmin;
  return (
    <div data-testid={`feedback-card-${post.id}`} className="rounded-2xl p-4" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
      <div className="flex gap-3">
      <button
        data-testid={`feedback-vote-${post.id}`}
        onClick={() => onVote(post)}
        className="flex flex-col items-center justify-center gap-0.5 rounded-xl px-3 py-2 self-start transition-transform active:scale-90"
        style={{ background: post.voted ? colors.brand : colors.surfaceTertiary, border: `1px solid ${post.voted ? "transparent" : colors.border}`, minWidth: 46 }}
      >
        <Heart size={16} color={post.voted ? colors.onBrand : colors.onSurfaceTertiary} fill={post.voted ? colors.onBrand : "none"} />
        <span className="font-mono2 font-bold text-[12.5px]" style={{ color: post.voted ? colors.onBrand : colors.onSurfaceTertiary }}>{post.vote_count}</span>
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <TypeIcon size={13} color={typeColor} />
          <span className="font-display font-bold text-[15px] leading-snug" style={{ color: colors.onSurface }}>{post.title}</span>
        </div>
        {post.description && (
          <p className="font-display text-[13px] leading-relaxed mb-2" style={{ color: colors.onSurfaceTertiary }}>{post.description}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={post.status} />
          <span className="font-display text-[11.5px]" style={{ color: colors.faint }}>{post.author?.display_name || "Someone"}</span>
          <button data-testid={`feedback-comments-toggle-${post.id}`} onClick={() => onToggleComments(post.id)} className="flex items-center gap-1 font-display text-[11.5px]" style={{ color: colors.brandSecondary }}>
            <MessageSquare size={12} color={colors.brandSecondary} /> {expanded ? "Hide" : "Comments"}
          </button>
          {canManage && (
            <>
              <button data-testid={`feedback-edit-${post.id}`} onClick={() => onEdit(post)} className="flex items-center gap-1 font-display text-[11.5px]" style={{ color: colors.onSurfaceTertiary }}>
                <Pencil size={12} color={colors.onSurfaceTertiary} /> Edit
              </button>
              <button data-testid={`feedback-delete-${post.id}`} onClick={() => onDelete(post)} className="flex items-center gap-1 font-display text-[11.5px]" style={{ color: "#F0A0A0" }}>
                <Trash2 size={12} color="#F0A0A0" /> Delete
              </button>
            </>
          )}
        </div>

        {isAdmin && (
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {STATUS_ORDER.map((st) => (
              <button
                key={st}
                data-testid={`feedback-set-${st}-${post.id}`}
                onClick={() => onStatus(post, st)}
                className="font-display font-medium text-[10.5px] rounded-full px-2.5 py-1 transition-colors"
                style={{ background: post.status === st ? colors.brand : colors.surfaceTertiary, color: post.status === st ? colors.onBrand : colors.onSurfaceTertiary, border: `1px solid ${colors.border}` }}
              >
                {STATUS[st].label}
              </button>
            ))}
          </div>
        )}
      </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
          <CommentThread parentType="feedback" parentId={post.id} accent={colors.brand} onColor={colors.onBrand} />
        </div>
      )}
    </div>
  );
}

export default function Feedback() {
  const [posts, setPosts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "feature_request", title: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listFeedback(filter);
      setPosts(res.posts || []);
      setIsAdmin(!!res.is_app_admin);
    } catch (e) {
      toast(e.message || "Couldn't load feedback");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => toast.dismiss(), []);

  const vote = async (post) => {
    setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, voted: !p.voted, vote_count: p.vote_count + (p.voted ? -1 : 1) } : p)));
    try { await api.voteFeedback(post.id); } catch { load(); }
  };

  const changeStatus = async (post, status) => {
    setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, status } : p)));
    try {
      await api.setFeedbackStatus(post.id, status);
      toast("Status updated");
    } catch (e) {
      toast(e.message || "Couldn't update status");
      load();
    }
  };

  const startEdit = (post) => {
    setForm({ type: post.type, title: post.title, description: post.description || "" });
    setEditingId(post.id);
    setCreating(true);
  };

  const deletePost = async (post) => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    setPosts((prev) => prev.filter((p) => p.id !== post.id));
    try { await api.deleteFeedback(post.id); toast("Post deleted"); }
    catch (e) { toast(e.message || "Couldn't delete"); load(); }
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await api.editFeedback(editingId, { type: form.type, title: form.title.trim(), description: form.description.trim() });
        toast("Post updated");
      } else {
        await api.createFeedback({ type: form.type, title: form.title.trim(), description: form.description.trim() });
        toast("Thanks — posted!");
      }
      setForm({ type: "feature_request", title: "", description: "" });
      setEditingId(null);
      setCreating(false);
      load();
    } catch (e) {
      toast(e.message || "Couldn't post");
    } finally {
      setSaving(false);
    }
  };

  const closeSheet = () => { setCreating(false); setEditingId(null); setForm({ type: "feature_request", title: "", description: "" }); };

  return (
    <TabLayout>
      <div className="px-4 pt-8" style={{ background: colors.surface }}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2.5">
            <HomeButton bg={colors.surfaceSecondary} />
            <h1 className="font-display font-bold text-[26px]" style={{ color: colors.onSurface }}>Feedback</h1>
          </div>
          <button data-testid="feedback-new-button" onClick={() => setCreating(true)} className="flex items-center gap-1.5 rounded-full py-2 px-4 mr-14" style={{ background: colors.brand }}>
            <Plus size={16} color={colors.onBrand} />
            <span className="font-display font-bold text-[13px]" style={{ color: colors.onBrand }}>Post</span>
          </button>
        </div>
        <p className="font-display text-[13px] mb-5" style={{ color: colors.onSurfaceTertiary }}>Report bugs and request features. Upvote what matters to you.</p>

        <div className="flex gap-2 mb-5">
          {TYPES.map((t) => {
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                data-testid={`feedback-filter-${t.key}`}
                onClick={() => setFilter(t.key)}
                className="flex items-center gap-1.5 rounded-full py-1.5 px-3.5 transition-colors"
                style={{ background: active ? colors.brand : colors.surfaceSecondary, border: `1px solid ${active ? "transparent" : colors.border}` }}
              >
                <t.icon size={13} color={active ? colors.onBrand : colors.onSurfaceTertiary} />
                <span className="font-display font-medium text-[12.5px]" style={{ color: active ? colors.onBrand : colors.onSurfaceTertiary }}>{t.label}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare size={30} color={colors.muted} className="mx-auto mb-3" />
            <p className="font-display font-medium text-[15px]" style={{ color: colors.onSurfaceTertiary }}>No posts yet</p>
            <p className="font-display text-[13px] mt-1" style={{ color: colors.faint }}>Be the first to share an idea or report a bug.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="feedback-list">
            {posts.map((p) => (
              <FeedbackCard key={p.id} post={p} isAdmin={isAdmin} onVote={vote} onStatus={changeStatus} onEdit={startEdit} onDelete={deletePost} expanded={expandedId === p.id} onToggleComments={(id) => setExpandedId((cur) => (cur === id ? null : id))} />
            ))}
          </div>
        )}
      </div>

      <Sheet visible={creating} onClose={closeSheet} title={editingId ? "Edit feedback" : "New feedback"} testID="feedback-create-sheet">
        <div className="flex gap-2 mb-4">
          {[{ k: "feature_request", l: "Feature request", i: Lightbulb }, { k: "bug", l: "Bug", i: Bug }].map((o) => {
            const active = form.type === o.k;
            return (
              <button
                key={o.k}
                data-testid={`feedback-type-${o.k}`}
                onClick={() => setForm((f) => ({ ...f, type: o.k }))}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 transition-colors"
                style={{ background: active ? colors.brand : colors.surfaceTertiary, border: `1px solid ${active ? "transparent" : colors.border}` }}
              >
                <o.i size={15} color={active ? colors.onBrand : colors.onSurfaceTertiary} />
                <span className="font-display font-medium text-[13px]" style={{ color: active ? colors.onBrand : colors.onSurfaceTertiary }}>{o.l}</span>
              </button>
            );
          })}
        </div>
        <Field label="Title">
          <Input testID="feedback-title-input" placeholder="Short summary" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        </Field>
        <Field label="Details (optional)">
          <textarea
            data-testid="feedback-description-input"
            placeholder="What happened, or what would you like to see?"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={4}
            className="w-full rounded-xl px-3.5 py-3 font-display text-[14px] outline-none resize-none"
            style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface }}
          />
        </Field>
        <PrimaryButton testID="feedback-submit-button" label={editingId ? "Save changes" : "Post feedback"} onClick={submit} disabled={!form.title.trim()} loading={saving} />
        <GhostButton label="Cancel" color={colors.onSurfaceTertiary} onClick={closeSheet} />
      </Sheet>
    </TabLayout>
  );
}
